import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import PanelBlock, { PanelEyebrow } from './PanelBlock';
import type { StructuredWorkup } from '../../types/laboklin';

/**
 * The engine's differential, and what the assistant set aside from it.
 *
 * Rank order only. The wireframe drew a confidence bar against each disease, and
 * that cannot ship: the engine's match scores are stripped before the assistant ever
 * sees them, deliberately, and a bar is a probability by another name. Position in
 * the list is the whole of what the engine claims, so position is what is shown.
 *
 * The set-aside block is not a footnote. The engine ranked those diseases ABOVE the
 * ones being kept, so disagreeing with it is the one judgement the veterinarian
 * cannot check for themselves - it needs the finding that contradicts each one, and
 * what would settle it, in front of them.
 */

interface DifferentialsPanelProps {
  workup: StructuredWorkup;
  waiting: boolean;
  progress: string | null;
}

export const DifferentialsPanel: React.FC<DifferentialsPanelProps> = ({
  workup,
  waiting,
  progress,
}) => {
  const { differentials, setAside, nextSteps, assumedPatientFields } = workup;

  if (waiting && differentials.length === 0) {
    return (
      <PanelBlock>
        <PanelEyebrow label="Differential" state="GekkoVet diagnostic engine" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1 }}>
          <CircularProgress size={14} thickness={5} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {progress ?? 'Working through the differential…'}
          </Typography>
        </Box>
      </PanelBlock>
    );
  }

  if (differentials.length === 0) return null;

  return (
    <PanelBlock>
      <PanelEyebrow
        label="Differential"
        state={`${differentials.length} shown, best first`}
      />

      {differentials.map((d) => (
        <Box
          key={d.diagnosisId}
          sx={{ py: 1, borderTop: '1px solid #eee', '&:first-of-type': { borderTop: 0 } }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{d.name}</Typography>
          {d.summary && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
              {d.summary}
            </Typography>
          )}
        </Box>
      ))}

      {setAside.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <PanelEyebrow label="Set aside, with reason" />
          {setAside.map((s) => (
            <Box key={s.diagnosisId} sx={{ py: 0.75, borderTop: '1px solid #eee' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {s.name}
                </Box>{' '}
                · {s.reason}
                {s.whatWouldSettleIt && ` ${s.whatWouldSettleIt}`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {nextSteps.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <PanelEyebrow label="What to do next" />
          {nextSteps.map((step, i) => (
            <Box key={i} sx={{ py: 0.75, borderTop: '1px solid #eee' }}>
              <Typography sx={{ fontSize: 13 }}>{step}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {assumedPatientFields.length > 0 && (
        // The engine requires an age, a weight and a sex whether or not the report
        // carries them, so extraction supplies them. Saying which ones were guessed
        // is the difference between a ranking the veterinarian can weigh and one
        // that quietly rests on an invented patient.
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1.5, fontStyle: 'italic' }}>
          The ranking assumed {assumedPatientFields.join(', ')} — the report gives none.
        </Typography>
      )}
    </PanelBlock>
  );
};

export default DifferentialsPanel;
